import type { LucideIcon } from "lucide-react";
import {
  BellRing,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleSlash,
  CirclePause,
  Clock,
  Pause,
  PhoneCall,
  PhoneForwarded,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Play,
  PowerOff,
  Square,
  Voicemail,
  XCircle,
  Ban,
} from "lucide-react";

/**
 * Tons visuais compartilhados por Badge/StatusBadge/StatTile/Avatar.
 * - semânticos: neutral, brand, success, warning, danger, info
 * - status de agente/ramal/chamada: online, paused, inuse, ringing, dialing, offline
 */
export type Tone =
  | "neutral"
  | "brand"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "online"
  | "paused"
  | "inuse"
  | "ringing"
  | "dialing"
  | "offline";

/** Classes literais por tom (o Tailwind precisa vê-las escritas no código-fonte). */
export const TONE_CLASSES: Record<Tone, { subtle: string; solid: string; dot: string; text: string; border: string }> = {
  neutral: {
    subtle: "bg-surface-sunken text-fg-muted",
    solid: "bg-fg-muted text-fg-inverse",
    dot: "bg-fg-subtle",
    text: "text-fg-muted",
    border: "border-border-strong",
  },
  brand: {
    subtle: "bg-brand-subtle text-brand-accent",
    solid: "bg-primary text-primary-fg",
    dot: "bg-brand-accent",
    text: "text-brand-accent",
    border: "border-brand-accent/40",
  },
  success: {
    subtle: "bg-success-subtle text-success-fg",
    solid: "bg-success text-on-success",
    dot: "bg-success",
    text: "text-success-fg",
    border: "border-success-border",
  },
  warning: {
    subtle: "bg-warning-subtle text-warning-fg",
    solid: "bg-warning text-on-warning",
    dot: "bg-warning",
    text: "text-warning-fg",
    border: "border-warning-border",
  },
  danger: {
    subtle: "bg-danger-subtle text-danger-fg",
    solid: "bg-danger text-on-danger",
    dot: "bg-danger",
    text: "text-danger-fg",
    border: "border-danger-border",
  },
  info: {
    subtle: "bg-info-subtle text-info-fg",
    solid: "bg-info text-on-info",
    dot: "bg-info",
    text: "text-info-fg",
    border: "border-info-border",
  },
  online: {
    subtle: "bg-status-online-subtle text-status-online-fg",
    solid: "bg-status-online text-status-online-on",
    dot: "bg-status-online",
    text: "text-status-online-fg",
    border: "border-status-online",
  },
  paused: {
    subtle: "bg-status-paused-subtle text-status-paused-fg",
    solid: "bg-status-paused text-status-paused-on",
    dot: "bg-status-paused",
    text: "text-status-paused-fg",
    border: "border-status-paused",
  },
  inuse: {
    subtle: "bg-status-inuse-subtle text-status-inuse-fg",
    solid: "bg-status-inuse text-status-inuse-on",
    dot: "bg-status-inuse",
    text: "text-status-inuse-fg",
    border: "border-status-inuse",
  },
  ringing: {
    subtle: "bg-status-ringing-subtle text-status-ringing-fg",
    solid: "bg-status-ringing text-status-ringing-on",
    dot: "bg-status-ringing",
    text: "text-status-ringing-fg",
    border: "border-status-ringing",
  },
  dialing: {
    subtle: "bg-status-dialing-subtle text-status-dialing-fg",
    solid: "bg-status-dialing text-status-dialing-on",
    dot: "bg-status-dialing",
    text: "text-status-dialing-fg",
    border: "border-status-dialing",
  },
  offline: {
    subtle: "bg-status-offline-subtle text-status-offline-fg",
    solid: "bg-status-offline text-status-offline-on",
    dot: "bg-status-offline",
    text: "text-status-offline-fg",
    border: "border-status-offline",
  },
};

/** Aceita os nomes antigos de tom (`default`) usados pelas páginas não migradas. */
export function normalizeTone(tone: string | undefined): Tone {
  if (!tone || tone === "default") return "neutral";
  return (tone in TONE_CLASSES ? tone : "neutral") as Tone;
}

export type StatusMeta = { label: string; tone: Tone; icon: LucideIcon; pulse?: boolean };

/* ——— Agente / ramal (DashPhone: ver shared/dashboard/phoneStatus.ts → rowStatus) ——— */
export type AgentStatus = "ONLINE" | "PAUSED" | "INUSE" | "RINGING" | "DIALING" | "OFFLINE";

export const AGENT_STATUS: Record<AgentStatus, StatusMeta> = {
  ONLINE: { label: "Disponível", tone: "online", icon: CircleCheck },
  PAUSED: { label: "Em pausa", tone: "paused", icon: CirclePause },
  INUSE: { label: "Em uso", tone: "inuse", icon: PhoneCall },
  RINGING: { label: "Tocando", tone: "ringing", icon: BellRing, pulse: true },
  DIALING: { label: "Discando", tone: "dialing", icon: PhoneOutgoing },
  OFFLINE: { label: "Offline", tone: "offline", icon: PowerOff },
};

/* ——— Chamada no softphone (softphoneStore CallState) ——— */
export const CALL_STATE: Record<"ringing" | "dialing" | "active" | "held" | "ended", StatusMeta> = {
  ringing: { label: "Tocando", tone: "ringing", icon: PhoneIncoming, pulse: true },
  dialing: { label: "Chamando", tone: "dialing", icon: PhoneOutgoing },
  active: { label: "Em chamada", tone: "inuse", icon: PhoneCall },
  held: { label: "Em espera", tone: "paused", icon: Pause },
  ended: { label: "Encerrada", tone: "offline", icon: PhoneOff },
};

/* ——— Registro SIP (softphoneStore RegState) ——— */
export const REG_STATE: Record<"idle" | "connecting" | "registered" | "reconnecting" | "failed", StatusMeta> = {
  idle: { label: "Desconectado", tone: "offline", icon: CircleSlash },
  connecting: { label: "Conectando…", tone: "warning", icon: CircleDashed, pulse: true },
  registered: { label: "Registrado", tone: "success", icon: CircleCheck },
  reconnecting: { label: "Reconectando…", tone: "warning", icon: CircleDashed, pulse: true },
  failed: { label: "Falha no registro", tone: "danger", icon: XCircle },
};

/* ——— Discador: campanha e contato (rótulos = features/outbound/outboundLabels.ts) ——— */
export const CAMPAIGN_STATUS_META: Record<"DRAFT" | "RUNNING" | "PAUSED" | "FINISHED", StatusMeta> = {
  DRAFT: { label: "Rascunho", tone: "neutral", icon: CircleDashed },
  RUNNING: { label: "Em execução", tone: "success", icon: Play, pulse: true },
  PAUSED: { label: "Pausada", tone: "warning", icon: Pause },
  FINISHED: { label: "Finalizada", tone: "info", icon: Square },
};

export const CONTACT_STATUS_META: Record<string, StatusMeta> = {
  PENDING: { label: "Pendente", tone: "neutral", icon: Clock },
  SCHEDULED: { label: "Reagendado", tone: "info", icon: Clock },
  DIALING: { label: "Discando", tone: "dialing", icon: PhoneOutgoing, pulse: true },
  ANSWERED: { label: "Atendeu", tone: "inuse", icon: PhoneCall },
  CONNECTED: { label: "Conectado", tone: "success", icon: PhoneForwarded },
  COMPLETED: { label: "Concluído", tone: "success", icon: CircleCheck },
  NO_ANSWER: { label: "Não atendeu", tone: "warning", icon: PhoneMissed },
  BUSY: { label: "Ocupado", tone: "warning", icon: CircleDot },
  FAILED: { label: "Falha", tone: "danger", icon: XCircle },
  MACHINE: { label: "Caixa postal", tone: "warning", icon: Voicemail },
  ABANDONED: { label: "Abandonada", tone: "danger", icon: PhoneOff },
  CANCELLED: { label: "Cancelado", tone: "neutral", icon: CircleSlash },
  DNC: { label: "Não ligar (DNC)", tone: "danger", icon: Ban },
};
